import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Ban } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function PaymentCancel() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [order, setOrder] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    api.get(`/payments/order-info/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [orderId]);

  const retryPayment = async () => {
    if (!orderId) return;
    setRetrying(true);
    try {
      const { data } = await api.post("/payments/checkout", { order_id: orderId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Zahlung konnte nicht gestartet werden.");
      setRetrying(false);
    }
  };

  const cancelOrder = async () => {
    if (!orderId) return;
    setCancelling(true);
    try {
      await api.post(`/orders/${orderId}/cancel-unpaid`);
      setCancelled(true);
      toast.success("Bestellung storniert.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Stornierung fehlgeschlagen.");
    } finally { setCancelling(false); }
  };

  return (
    <section data-testid="payment-cancel" className="relative z-10 bg-[#050505] py-20 lg:py-28 min-h-[70vh]">
      <div className="max-w-2xl mx-auto px-6 lg:px-10">
        <div className="glass rounded-3xl p-10 lg:p-14">
          <AlertTriangle size={44} className="text-amber-400 mb-6" />
          <h2 className="font-display text-4xl lg:text-5xl">Zahlung abgebrochen.</h2>

          {cancelled ? (
            <>
              <p data-testid="payment-cancel-confirmed" className="text-[#aaa] mt-4 leading-relaxed">
                Deine Bestellung wurde storniert. Es wurde nichts abgebucht.
              </p>
              <Link to="/" data-testid="payment-cancel-home" className="mt-8 inline-block text-sm text-white underline underline-offset-4 hover:text-[#ccc] transition-colors">
                Zurück zur Startseite
              </Link>
            </>
          ) : (
            <>
              <p className="text-[#aaa] mt-4 leading-relaxed">
                Deine Zahlung wurde nicht abgeschlossen. Deine Bestellung
                {order?.reference ? <> <span className="text-white font-mono">{order.reference}</span></> : ""} ist noch gespeichert, aber
                noch unbezahlt. Du kannst die Zahlung jetzt erneut versuchen oder die Bestellung stornieren.
              </p>

              {order && (
                <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 text-sm">
                  <div className="flex justify-between"><span className="text-[#888]">Reparatur</span><span className="text-white">{order.service_label} · {order.device}</span></div>
                  <div className="flex justify-between mt-2"><span className="text-[#888]">Betrag</span><span className="font-mono text-white">{order.total.toFixed(2)} €</span></div>
                </div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button onClick={retryPayment} disabled={retrying} data-testid="payment-retry-button"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#050505] py-3.5 text-sm font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
                  <RefreshCw size={16} /> {retrying ? "Wird gestartet…" : "Zahlung erneut versuchen"}
                </button>
                <button onClick={cancelOrder} disabled={cancelling} data-testid="payment-cancel-order-button"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white py-3.5 text-sm font-medium hover:bg-white/5 transition-colors disabled:opacity-50">
                  <Ban size={16} /> {cancelling ? "Wird storniert…" : "Bestellung stornieren"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
